const NUMERIC_FIELD_TYPES = new Set([
    'number',
    'integer',
    'long',
    'double',
    'float',
    'decimal',
    'numberfield',
    'currencyfield',
    'amountfield',
    'percentageinput',
]);

export const DEFAULT_NUMERIC_MAX_VALUE = 2147483647;

const normalizeToken = (value) => String(value || '').trim().toLowerCase();

const toFiniteNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }

    const normalizedValue = typeof value === 'string'
        ? value.replace(/,/g, '').trim()
        : value;

    if (normalizedValue === '') {
        return null;
    }

    const numericValue = Number(normalizedValue);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const isNumericFieldConfig = (fieldConfig = {}) => (
    NUMERIC_FIELD_TYPES.has(normalizeToken(fieldConfig?.valueType))
    || NUMERIC_FIELD_TYPES.has(normalizeToken(fieldConfig?.type))
    || NUMERIC_FIELD_TYPES.has(normalizeToken(fieldConfig?.uiType))
);

export const resolveNumericFieldConstraints = (fieldConfig = {}) => {
    const explicitMaxValue = toFiniteNumber(fieldConfig?.maxValue);
    const explicitMinValue = toFiniteNumber(fieldConfig?.minValue);
    const isNumericField = isNumericFieldConfig(fieldConfig);

    return {
        isNumericField,
        maxValue: explicitMaxValue !== null
            ? explicitMaxValue
            : (isNumericField ? DEFAULT_NUMERIC_MAX_VALUE : null),
        minValue: explicitMinValue,
    };
};

export const getNumericRangeError = (fieldConfig = {}, value, label) => {
    const numericValue = toFiniteNumber(value);
    if (numericValue === null) {
        return '';
    }

    const { isNumericField, maxValue, minValue } = resolveNumericFieldConstraints(fieldConfig);
    if (!isNumericField) {
        return '';
    }

    const resolvedLabel = typeof label === 'string' && label.trim()
        ? label.trim()
        : 'Value';

    if (maxValue !== null && numericValue > maxValue) {
        return fieldConfig?.errorTypes?.maxValue || `${resolvedLabel} cannot exceed ${maxValue}.`;
    }

    if (minValue !== null && numericValue < minValue) {
        return fieldConfig?.errorTypes?.minValue || `${resolvedLabel} cannot be less than ${minValue}.`;
    }

    return '';
};

export const sanitizeNumericInputValue = (rawValue, fieldConfig = {}) => {
    const normalizedValue = typeof rawValue === 'string'
        ? rawValue.replace(/,/g, '').trim()
        : rawValue;

    if (normalizedValue === '') {
        return {
            isValid: true,
            numericValue: '',
        };
    }

    if (typeof fieldConfig?.uiRegex === 'string' && fieldConfig.uiRegex) {
        const inputPattern = new RegExp(fieldConfig.uiRegex);
        if (!inputPattern.test(String(normalizedValue))) {
            return {
                isValid: false,
                error: fieldConfig?.errorTypes?.format || fieldConfig?.error || 'Invalid format.',
            };
        }
    }

    const numericValue = toFiniteNumber(normalizedValue);
    if (numericValue === null) {
        return {
            isValid: false,
            error: 'Please enter a valid number.',
        };
    }

    const rangeError = getNumericRangeError(fieldConfig, numericValue, fieldConfig?.label);
    if (rangeError) {
        return {
            isValid: false,
            error: rangeError,
        };
    }

    return {
        isValid: true,
        numericValue,
    };
};
